import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { GALLERY, CATEGORIES } from "@/data/site";

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  return (
    <main data-testid="gallery-page" className="pt-20">
      <Seo
        title="Gallery — Eunice 25 | Our Painting Work in Oldham"
        siteName="Eunice 25"
        description="Browse real painting transformations by Eunice 25 — interior walls, ceilings, frames, skirting and staining across Oldham."
        image="/assets/work2.jpg"
      />

      <section className="bg-[#121212] text-[#fbf9f5] grain relative py-24 sm:py-32" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">Our Work</p>
            <h1 className="mt-4 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              The proof is on <span className="italic text-[#c8102e]">the walls.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="bg-[#fbf9f5] py-20 sm:py-28" data-testid="gallery-grid-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap gap-3" data-testid="gallery-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  filter === c
                    ? "bg-[#c8102e] text-white"
                    : "bg-white border border-black/10 text-[#121212] hover:border-[#c8102e] hover:text-[#c8102e]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
            <AnimatePresence mode="popLayout">
              {items.map((g) => (
                <motion.button
                  layout
                  key={g.id}
                  data-testid={`gallery-item-${g.id}`}
                  onClick={() => setLightbox(g)}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="group relative rounded-2xl overflow-hidden text-left bg-white border border-black/10"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={g.src}
                      alt={g.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#c8102e] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Expand className="w-4 h-4" />
                  </div>
                  <div className="p-5">
                    <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#c8102e]">{g.category}</p>
                    <h3 className="mt-1 font-display font-bold text-lg tracking-tight">{g.title}</h3>
                    <p className="font-mono2 text-xs text-neutral-500 mt-1">{g.spec}</p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#121212]/95 backdrop-blur-sm flex items-center justify-center p-5"
            onClick={() => setLightbox(null)}
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 24 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.src}
                alt={lightbox.title}
                className="w-full max-h-[70vh] object-contain rounded-2xl"
              />
              <figcaption className="mt-5 flex items-start justify-between gap-4 text-[#fbf9f5]">
                <div>
                  <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#c8102e]">{lightbox.category}</p>
                  <h3 className="font-display font-bold text-2xl mt-1">{lightbox.title}</h3>
                  <p className="font-mono2 text-xs text-[#fbf9f5]/60 mt-1">{lightbox.spec}</p>
                </div>
                <button
                  data-testid="lightbox-close-btn"
                  onClick={() => setLightbox(null)}
                  className="shrink-0 w-11 h-11 rounded-full border border-white/30 flex items-center justify-center hover:bg-[#c8102e] hover:border-[#c8102e] transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
